/**
 * Header icon + drawer for the cross-sell job queue (UX_SPEC_F1_DELTA §5.3).
 *
 * Badge shows pending + unviewed-completed (`selectTotalBadge`); click opens an
 * Ant Drawer listing in-flight + recently-finished jobs. Generalises the
 * `EnrichmentQueueIcon` popover into a wider drawer because it carries multiple
 * job types. Only rendered (in Header) when the `cross_sell` feature is on.
 */
import React, { useEffect, useState } from 'react';
import { Badge, Drawer, Empty, Progress, Button, Alert } from 'antd';
import { Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useCrossSellQueueStore,
  selectTotalBadge,
  selectUnviewedCompleted,
  type CrossSellQueueEntry,
} from '../stores/useCrossSellQueueStore';

const STATUS_ICON: Record<CrossSellQueueEntry['status'], string> = {
  processing: '🔄',
  completed: '✅',
  failed: '❌',
  cancelled: '⊘',
};

const QueueRow: React.FC<{
  entry: CrossSellQueueEntry;
  onNavigate: () => void;
}> = ({ entry, onNavigate }) => {
  const { t } = useTranslation();
  const removeOnClick = useCrossSellQueueStore((s) => s.removeOnClick);

  return (
    <div style={{ padding: '10px 4px', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {STATUS_ICON[entry.status]} {t('crossSell.queue.jobImporting', { label: entry.label })}
      </div>
      {entry.status === 'processing' && (
        <Progress
          percent={Math.round(entry.progress ?? 0)}
          size="small"
          status="active"
          strokeColor="#6366F1"
        />
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <Button type="link" size="small" style={{ padding: 0 }} onClick={onNavigate}>
          {t('crossSell.queue.viewDetail')}
        </Button>
        {entry.status !== 'processing' && (
          <Button
            type="text"
            size="small"
            style={{ marginLeft: 'auto' }}
            onClick={() => removeOnClick(entry.jobId)}
          >
            {t('crossSell.queue.dismiss')}
          </Button>
        )}
      </div>
    </div>
  );
};

const CrossSellQueueIcon: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const entries = useCrossSellQueueStore((s) => s.entries);
  const total = useCrossSellQueueStore(selectTotalBadge);
  const unviewedDone = useCrossSellQueueStore(selectUnviewedCompleted);
  const markAllViewed = useCrossSellQueueStore((s) => s.markAllViewed);
  const streamMode = useCrossSellQueueStore((s) => s.streamMode);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && unviewedDone > 0) markAllViewed();
  }, [open, unviewedDone, markAllViewed]);

  const isEmpty = entries.length === 0;

  return (
    <>
      <Badge count={total} size="small" styles={{ indicator: { background: '#6366F1' } }}>
        <button
          aria-label={t('crossSell.queue.iconLabel')}
          onClick={() => setOpen(true)}
          className="flex items-center justify-center p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer border-0 bg-transparent"
          style={{ color: 'var(--color-text-secondary)', opacity: isEmpty ? 0.55 : 1 }}
          data-testid="cross-sell-queue-icon"
        >
          <Package size={20} />
        </button>
      </Badge>

      <Drawer
        title={t('crossSell.queue.drawerTitle')}
        placement="right"
        width={400}
        open={open}
        onClose={() => setOpen(false)}
      >
        {streamMode === 'polling' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t('crossSell.queue.offlineWarning')}
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                {t('crossSell.queue.reconnect')}
              </Button>
            }
          />
        )}
        {isEmpty ? (
          <Empty description={t('crossSell.queue.empty')} />
        ) : (
          entries.map((e) => (
            <QueueRow
              key={e.jobId}
              entry={e}
              onNavigate={() => {
                navigate(`/cross-sell/import/${e.jobId}`);
                setOpen(false);
              }}
            />
          ))
        )}
      </Drawer>
    </>
  );
};

export default CrossSellQueueIcon;
